import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  CreditCard, 
  TrendingDown,
  Calendar,
  DollarSign,
  Target,
  Zap,
  Snowflake,
  Calculator,
  CheckCircle2,
  Info
} from "lucide-react";
import { toast } from "sonner";

export const DebtPayoffPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [payoffPlan, setPayoffPlan] = useState(null);
  const [extraPayment, setExtraPayment] = useState(0);
  const [strategy, setStrategy] = useState("avalanche");
  const [calculating, setCalculating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, planRes] = await Promise.all([
        api.get("/debt/accounts"),
        api.post(`/debt/payoff-plan?extra_payment=0&strategy=${strategy}`)
      ]);
      setAccounts(accountsRes.data);
      setPayoffPlan(planRes.data);
    } catch (error) {
      console.error("Failed to fetch debt data:", error);
      toast.error("Failed to load debt information");
    } finally {
      setLoading(false);
    }
  }, [api, strategy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculatePlan = async () => {
    setCalculating(true);
    try {
      const response = await api.post(`/debt/payoff-plan?extra_payment=${extraPayment}&strategy=${strategy}`);
      setPayoffPlan(response.data);
      toast.success("Plan updated!");
    } catch (error) {
      toast.error("Failed to calculate plan");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const [year, month] = dateStr.split("-");
    return new Date(year, parseInt(month) - 1).toLocaleDateString("en-CA", {
      month: "long",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="debt-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const hasDebt = accounts.length > 0 && accounts.some(a => a.balance > 0);

  return (
    <div data-testid="debt-payoff-page">
      <div className="phead"><div><h1>Debt Payoff Plan</h1><div className="sub">Build a strategy to become debt-free</div></div></div>
      <AISummary page="debt" />

      {!hasDebt ? (
        <div className="card" style={{padding:72,textAlign:"center"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
            <div style={{width:56,height:56,borderRadius:16,background:"#DCEAD8",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}><CheckCircle2 size={26} stroke="#3F7D52" /></div>
            <div className="serif" style={{fontSize:23,fontWeight:600,color:"#3F7D52"}}>You're debt-free!</div>
            <div style={{maxWidth:360,color:"#7d7d70"}}>Great job! You don't have any credit card debt to pay off.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:24}}>
            <div className="card" style={{padding:"24px 26px"}}>
              <div className="ai2" style={{background:"rgba(176,96,63,.08)",marginBottom:14}}><CreditCard size={21} stroke="#B0603F" /></div>
              <div className="lab">Total debt</div>
              <div className="serif" style={{fontSize:32,fontWeight:600,color:"#B0603F",marginTop:6}}>{formatCurrency(payoffPlan?.total_debt || 0)}</div>
            </div>
            <div className="card" style={{padding:"24px 26px"}}>
              <div className="ai2" style={{marginBottom:14}}><DollarSign size={21} stroke="#3F7D52" /></div>
              <div className="lab">Monthly payment</div>
              <div className="serif" style={{fontSize:32,fontWeight:600,marginTop:6}}>{formatCurrency(payoffPlan?.total_minimum || 0)}</div>
            </div>
            <div className="card" style={{padding:"24px 26px"}}>
              <div className="ai2" style={{background:"#e4f0dd",marginBottom:14}}><Target size={21} stroke="#3F7D52" /></div>
              <div className="lab">Debt-free date</div>
              <div className="serif" style={{fontSize:32,fontWeight:600,color:"#3F7D52",marginTop:6}}>{payoffPlan?.debt_free_date || "—"}</div>
            </div>
          </div>

          {/* Strategy Selection */}
          <Card className="border-border/50">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold mb-4">Choose Your Strategy</h2>
              
              <Tabs value={strategy} onValueChange={setStrategy} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="avalanche" className="gap-2" data-testid="avalanche-tab">
                    <Zap className="w-4 h-4" />
                    Avalanche
                  </TabsTrigger>
                  <TabsTrigger value="snowball" className="gap-2" data-testid="snowball-tab">
                    <Snowflake className="w-4 h-4" />
                    Snowball
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="avalanche" className="mt-0">
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Debt Avalanche Method</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Pay minimum on all debts, then put extra money toward the highest interest rate debt first. 
                          This method saves the most money on interest.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="snowball" className="mt-0">
                  <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                      <Snowflake className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <p className="font-medium">Debt Snowball Method</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Pay minimum on all debts, then put extra money toward the smallest balance first. 
                          This method provides quick wins and motivation.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Extra Payment Calculator */}
          <Card className="border-border/50">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Extra Payment Calculator
              </h2>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Extra Monthly Payment</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      value={extraPayment}
                      onChange={(e) => setExtraPayment(parseFloat(e.target.value) || 0)}
                      className="pl-7"
                      min={0}
                      data-testid="extra-payment-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Amount above minimum payments you can contribute
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="invisible">Action</Label>
                  <Button 
                    onClick={calculatePlan} 
                    disabled={calculating}
                    className="w-full rounded-full"
                    data-testid="calculate-btn"
                  >
                    {calculating ? (
                      <>
                        <Calculator className="mr-2 h-4 w-4 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        <Calculator className="mr-2 h-4 w-4" />
                        Update Plan
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground invisible">
                    Placeholder for alignment
                  </p>
                </div>
              </div>
              
              {payoffPlan?.total_interest > 0 && (
                <div className="mt-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-400">Interest Projection</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        At current payments, you&apos;ll pay approximately {formatCurrency(payoffPlan.total_interest)} in interest.
                        Adding extra payments can significantly reduce this amount.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Debt Accounts */}
          <section>
            <h2 className="section-label">Your Debts ({strategy === "avalanche" ? "Highest Interest First" : "Smallest Balance First"})</h2>
            <div className="space-y-3">
              {payoffPlan?.accounts?.map((account, index) => (
                <Card key={account.name} className="border-border/50" data-testid={`debt-account-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          index === 0 
                            ? "bg-primary/10" 
                            : "bg-muted"
                        }`}>
                          <span className="font-bold text-sm tabular-nums">{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-semibold">{account.name}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                            <span>{account.interest_rate}% APR</span>
                            <span>Min: {formatCurrency(account.minimum_payment)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums text-red-600 dark:text-red-400">
                          {formatCurrency(account.balance)}
                        </p>
                        {index === 0 && (
                          <span className="text-xs text-primary font-medium">Focus here</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};
